import type { ReactNode } from "react"
import { Link, usePage } from "@inertiajs/react"
import { Bell, CircleHelp, House, LogOut } from "lucide-react"
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar"
import { DriverHelpButton, topBarIconClass } from "@/components/driver-help"
import { GUIDE_PUSH, GuidePanelProvider, useGuidePanel } from "@/components/guided-video"
import { TOUR_RING, TourSpotlightProvider, useTourSpotlight } from "@/components/tour-spotlight"
import { cn } from "@/lib/utils"
import { KioskLayout, type KioskProps } from "./KioskLayout"
import { SharedFormsLayout, type SharedFormsProps } from "./SharedFormsLayout"
import { AdminSidebar, isActive, type NavCounts, type NavRoutes } from "./Sidebar"

type Props = { children: ReactNode }

type SharedProps = {
  routes: NavRoutes & {
    home: string
    logout: string | null
    notifications?: string | null
    help?: string | null
    /** Drivers only — their hub, and the Help button's endpoint. */
    driver_home?: string | null
    create_help_request?: string | null
    /** Password-holders (admins, super-admins) only — their own account. */
    account?: string | null
  }
  auth: {
    authenticated: boolean
    user: {
      email: string
      name: string
      /** How this person signs in — not what they can reach. See `nav`. */
      role: string
      /** Which sidebar to draw: "admin" for anyone holding a module, else "member". */
      nav: "admin" | "member"
      organization: { name: string } | null
    } | null
  }
  /** Absent for users with no admin surfaces — the nav simply carries no counts. */
  nav_counts?: NavCounts
  /** Admins only — the bell's unread badge. */
  notifications?: { unread: number } | null
  /** Present only while an admin is impersonating a demo driver. */
  impersonation?: { name: string; endPath: string } | null
  /** Present only inside an office-kiosk session — swaps in the kiosk shell. */
  kiosk?: KioskProps | null
  /** Present only on the shared "bluebird" login — swaps in the stripped shell. */
  shared_forms?: SharedFormsProps | null
}

const linkClass = "text-muted-foreground transition-colors hover:text-foreground"

function NotificationBell({ href, unread, active }: { href: string; unread: number; active: boolean }) {
  return (
    <Link
      href={href}
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
      className={cn("relative", topBarIconClass(active))}
    >
      <Bell className="h-5 w-5" aria-hidden="true" />
      {unread > 0 && (
        <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground tabular-nums">
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </Link>
  )
}

// The tour spotlight has to sit above both the top bar and the page: the page
// owns the video and says what's being narrated, and the top bar lights up its
// own two buttons when Emma reaches them. The guided-video panel is the same
// shape: the page opens it, and the layout makes room for it.
export function AppLayout({ children }: Props) {
  return (
    <TourSpotlightProvider>
      <GuidePanelProvider>
        <AppShell>{children}</AppShell>
      </GuidePanelProvider>
    </TourSpotlightProvider>
  )
}

function AppShell({ children }: Props) {
  const page = usePage<SharedProps>()
  const { target: spotlight } = useTourSpotlight()
  const { open: guideOpen } = useGuidePanel()
  const { routes, auth, nav_counts, notifications, impersonation, kiosk, shared_forms } = page.props

  // A kiosk session gets the full-bleed shell instead of the sidebar app — every
  // page a driver can reach from the kiosk picks this up without knowing about
  // it. The server only shares `kiosk` inside such a session.
  if (kiosk) {
    return <KioskLayout kiosk={kiosk}>{children}</KioskLayout>
  }

  // Same idea for the driver who came through the Christmas tree: no account, so
  // no sidebar to build and nothing in the top bar that would work. The server
  // shares this only on the shared credential — an account always wins and keeps
  // the ordinary shell.
  if (shared_forms) {
    return <SharedFormsLayout shared={shared_forms}>{children}</SharedFormsLayout>
  }

  return (
    <SidebarProvider>
      <AdminSidebar
        routes={routes}
        counts={nav_counts ?? {}}
        currentPath={page.url}
        nav={auth.user?.nav}
        footer={
          auth.authenticated && routes.logout ? (
            <Link
              href={routes.logout}
              method="delete"
              as="button"
              className="rounded text-sm text-sidebar-foreground/80 transition-colors hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            >
              Sign out
            </Link>
          ) : (
            <span className="text-sm text-sidebar-foreground/80">Driver Compliance</span>
          )
        }
      />

      {/* While a guided video is open the page moves left out from under it
          (on a phone, down from under it). Between the two it covers the page,
          as there isn't room for both side by side. */}
      <SidebarInset className={cn(guideOpen && GUIDE_PUSH)}>
        {impersonation && (
          <div className="flex flex-wrap items-center justify-between gap-2 bg-warning px-4 py-2 text-sm text-warning-foreground">
            <span>
              Viewing as demo driver <strong>{impersonation.name}</strong>
            </span>
            <Link
              href={impersonation.endPath}
              method="delete"
              as="button"
              className="inline-flex items-center gap-1.5 rounded-md bg-warning-foreground/10 px-2.5 py-1 font-medium underline-offset-2 hover:bg-warning-foreground/20"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Return to my admin account
            </Link>
          </div>
        )}
        {/* The top bar is now almost empty on purpose: the nav moved to the
            sidebar, which leaves this for per-page and per-user things —
            notifications, an org switcher, a user menu. */}
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background px-4">
          <SidebarTrigger className={linkClass} />
          <div className="ml-auto flex items-center gap-3 text-sm">
            {/* The signed-in identity doubles as the way into the account page
                for anyone who has one — a name to sign with, an email and
                password to change. Drivers and staff keep the plain label. */}
            {auth.user?.name &&
              (routes.account ? (
                <Link
                  href={routes.account}
                  className={cn("hidden sm:inline", linkClass)}
                  title="Your account"
                >
                  {auth.user.name}
                </Link>
              ) : (
                <span className="hidden text-muted-foreground sm:inline">{auth.user.name}</span>
              ))}
            {/* A driver's way back. The sidebar carries Home too, but it's
                off-canvas on a phone — which is exactly where someone gets
                lost mid-packet. */}
            {routes.driver_home && (
              <Link
                href={routes.driver_home}
                aria-label="Home"
                className={cn(
                  topBarIconClass(isActive(page.url, routes.driver_home)),
                  spotlight === "chrome" && TOUR_RING,
                )}
              >
                <House className="h-5 w-5" aria-hidden="true" />
              </Link>
            )}
            {/* Drivers get the "ask the office" box (the Driver Guide is inside
                it); everyone else keeps the plain link to the guide. */}
            {routes.create_help_request ? (
              <DriverHelpButton
                createPath={routes.create_help_request}
                guidePath={routes.help}
                className={cn(spotlight === "chrome" && TOUR_RING)}
              />
            ) : (
              routes.help && (
                <Link
                  href={routes.help}
                  aria-label="Help & guide"
                  className={topBarIconClass(isActive(page.url, routes.help))}
                >
                  <CircleHelp className="h-5 w-5" aria-hidden="true" />
                </Link>
              )
            )}
            {routes.notifications && (
              <NotificationBell
                href={routes.notifications}
                unread={notifications?.unread ?? 0}
                active={isActive(page.url, routes.notifications)}
              />
            )}
          </div>
        </header>

        {/* min-w-0: flex-1 alone keeps min-width:auto, so a wide child (a nowrap
            row, a long word) refuses to shrink and pushes the page sideways on a
            phone. This lets the content column shrink to the viewport. */}
        <main className="min-w-0 flex-1">{children}</main>
      </SidebarInset>
    </SidebarProvider>
  )
}
